(ns manage.content-configuration-page.context-source.schema
  (:require [clojure.set :refer [rename-keys union]]
            [common.content-function.schemas :as old]
            [manage.adset.util :as adset-util]
            [re-frame.core :as rf]))

(def ^:private icon
  {"urn:lemonpi:schema:context:instagram:v1" "photo_camera"
   "urn:lemonpi:schema:context:creative:v1" "art_track"
   "urn:lemonpi:schema:context:custom:v1" "gavel"
   "urn:lemonpi:schema:context:datetime:v1" "schedule"
   "urn:lemonpi:schema:context:device:v1" "devices"
   "urn:lemonpi:schema:context:dsp-signal:v1" "cloud_sync"
   "urn:lemonpi:schema:context:gdpr:v1" "lock"
   "urn:lemonpi:schema:context:ias-contextual:v1" "local_library"
   "urn:lemonpi:schema:context:location:v1" "pin_drop"
   "urn:lemonpi:schema:context:locale:v1" "public"
   "urn:lemonpi:schema:context:products:v1" "shopping_basket"
   "urn:lemonpi:schema:context:split-testing:v2" "science"
   "urn:lemonpi:schema:context:weather:v1" "wb_sunny"
   "urn:lemonpi:schema:context:poi:v1" "sensors"
   "urn:lemonpi:schema:context:content-variant:v1" "dynamic_feed"})

(def ^:private chip
  {"urn:lemonpi:schema:context:ias-contextual:v1" "$0.02 CPM"})

(def default-icon "extension")

(def ^:private video-set #{:video :ctv})
(def ^:private display-set #{:display/content-variants :display/rules})
(def ^:private display+video-set (union display-set video-set))
(def ^:private display-rules-set #{:display/rules})
(def ^:private all-set #{:all})

(defn- get-visibility-map [enable-ias-contextual?]
  {"urn:lemonpi:schema:context:adform:v1" (union display-set #{:video})
   "urn:lemonpi:schema:context:appnexus:v1" display+video-set
   "urn:lemonpi:schema:context:creative:v1" display-rules-set
   "urn:lemonpi:schema:context:content-variant:v1" all-set
   "urn:lemonpi:schema:context:datetime:v1" all-set
   "urn:lemonpi:schema:context:device:v1" (union display+video-set #{:youtube})
   "urn:lemonpi:schema:context:dsp-signal:v1" display+video-set
   "urn:lemonpi:schema:context:gdpr:v1" display+video-set
   "urn:lemonpi:schema:context:google-cm:v1" (union display-set #{:video})
   "urn:lemonpi:schema:context:ias-contextual:v1" (if enable-ias-contextual? (union display-set #{:video}) #{})
   "urn:lemonpi:schema:context:locale:v1" display+video-set
   "urn:lemonpi:schema:context:location:v1" (union display+video-set #{:youtube})
   "urn:lemonpi:schema:context:split-testing:v2" display+video-set
   "urn:lemonpi:schema:context:weather:v1" all-set
   "urn:lemonpi:schema:context:poi:v1" display+video-set
   "urn:lemonpi:schema:context:products:v1" display-rules-set})

(defn schema-id->icon [schema-id]
  (icon schema-id default-icon))

(defn get-schema-options
  [schemas used-ids]
  (sort-by :label
           (for [{:keys [id label parameter]} schemas
                 :when (or (and (= "required" parameter)
                                ;; unlike other schema's with :parameter "required",
                                ;; only one dsp-signal can be added
                                (not= id "urn:lemonpi:schema:context:dsp-signal:v1"))
                           (not (contains? (set used-ids) id)))]
             {:label label
              :value id
              :icon (schema-id->icon id)
              :chip (chip id)})))

(defn get-adset-channel-type
  [adset]
  (let [target-type (or (:targetType adset) "dsp") ; falling back to dsp if targetType is nil
        adset-channel-type (adset-util/target-type->channel target-type)
        display? (= adset-channel-type :display)]
    (if display?
      (keyword adset-channel-type (:workflow adset))
      adset-channel-type)))

(rf/reg-sub ::schema-options-filtered-by-adset-type
  :<- [:resource.schema/schemas]
  :<- [:manage.content-configuration-page.context-source.db/used-schemas-ids]
  :<- [:resource.adset/one]
  :<- [:flag/enabled :content-function-ias-contextual]
  (fn [[schemas used-ids adset enable-ias-contextual?] _]
    (let [unused-schemas (get-schema-options schemas used-ids)
          adset-channel-type (get-adset-channel-type (-> adset :value))
          visibility-map (get-visibility-map enable-ias-contextual?)]
      (->> unused-schemas
           (filter #(or
                     (contains? (-> % :value visibility-map) :all)
                     (contains? (-> % :value visibility-map) adset-channel-type)))))))

;; proxy for old schema API client library, to ease migration
(def find-type old/find-type)
(def find-property old/find-property)

(defn find-values
  "List possible value options for property matching `selector` in `schema`."
  [selector schema]
  (letfn [(make-options [p]
            (case (:type p)
              "set" (make-options (:items p))
              "option" (map #(rename-keys % {:id :value}) (:values p))
              "enum" (map #(hash-map :value % :label %) (:values p))
              []))]
    (make-options (find-property selector schema))))

(defn get-type
  [schema]
  (case (:type schema)
    "object" ["object" (into {} (map (fn [[k v]]
                                       [k (get-type v)])
                                     (:properties schema)))]
    "list" ["list" (get-type (:items schema))]
    "set" ["set" (get-type (:items schema))]
    (:type schema)))

(defn selector->schema
  [selector selector-schemas]
  (loop [path (rest selector)
         m (get selector-schemas (first selector))]
    (if (seq path)
      (let [[head & tail] path
            type (:type m)]
        (recur tail
               (case type
                 "list" (:items m)
                 "set" (:items m)
                 "object" (get-in m [:properties head])
                 (when m (m head)))))
      m)))
